import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  DASHBOARD_COMPONENT_KEYS,
  DASHBOARD_COMPONENT_LABELS,
  COMPONENT_ROLE_RESTRICTIONS,
  type DashboardComponentKey,
} from "@/lib/dashboard-visibility";

interface AdminVisibilityConfig {
  config: Partial<Record<string, DashboardComponentKey[]>>;
  componentKeys: DashboardComponentKey[];
}

const MANAGED_ROLES = [
  { slug: "staff", label: "Staff Manager" },
  { slug: "staff-coordinator", label: "Staff Coordinator" },
  { slug: "ops", label: "Operations" },
  { slug: "admin", label: "Admin" },
] as const;

interface RoleVisibilityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RoleVisibilityDialog({ open, onOpenChange }: RoleVisibilityDialogProps) {
  const { toast } = useToast();

  const { data, isLoading } = useQuery<AdminVisibilityConfig>({
    queryKey: ["/api/admin/app-settings/dashboard-visibility"],
    enabled: open,
  });

  const [localConfig, setLocalConfig] = useState<Partial<Record<string, DashboardComponentKey[]>>>({});

  useEffect(() => {
    if (data) {
      const initial: Partial<Record<string, DashboardComponentKey[]>> = {};
      for (const role of MANAGED_ROLES) {
        initial[role.slug] = data.config[role.slug] ?? [...DASHBOARD_COMPONENT_KEYS];
      }
      setLocalConfig(initial);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async (config: Partial<Record<string, DashboardComponentKey[]>>) => {
      return await apiRequest("PUT", "/api/admin/app-settings/dashboard-visibility", { config });
    },
    onSuccess: () => {
      toast({ title: "Saved", description: "Dashboard visibility updated." });
      queryClient.invalidateQueries({ queryKey: ["/api/app-settings/dashboard-visibility"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/app-settings/dashboard-visibility"] });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save visibility config",
        variant: "destructive",
      });
    },
  });

  const toggleKey = (roleSlug: string, key: DashboardComponentKey) => {
    setLocalConfig((prev) => {
      const current = prev[roleSlug] ?? [...DASHBOARD_COMPONENT_KEYS];
      const next = current.includes(key)
        ? current.filter((k) => k !== key)
        : [...current, key];
      return { ...prev, [roleSlug]: next };
    });
  };

  const isChecked = (roleSlug: string, key: DashboardComponentKey) => {
    const keys = localConfig[roleSlug];
    if (!keys) return true;
    return keys.includes(key);
  };

  const handleSave = () => {
    saveMutation.mutate(localConfig);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Dashboard Component Visibility</DialogTitle>
          <DialogDescription>
            Control which dashboard and navigation components are visible for each role. Unchecking a component hides it from users with that role.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4 py-2">
            {[1, 2].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-5 w-24" />
                <div className="grid grid-cols-2 gap-2">
                  {[1, 2, 3, 4].map((j) => (
                    <Skeleton key={j} className="h-8 w-full" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-2 space-y-6">
            <div>
              <h3 className="text-sm font-semibold mb-1">Super Admin</h3>
              <p className="text-xs text-muted-foreground mb-3">Super admins always have full visibility and are not affected by this configuration.</p>
              <div className="grid grid-cols-2 gap-2 opacity-50 pointer-events-none">
                {DASHBOARD_COMPONENT_KEYS.map((key) => (
                  <div key={key} className="flex items-center gap-2 rounded-md border px-3 py-2">
                    <Checkbox checked disabled />
                    <Label className="text-sm select-none">{DASHBOARD_COMPONENT_LABELS[key]}</Label>
                  </div>
                ))}
              </div>
            </div>

            {MANAGED_ROLES.map((role) => (
              <div key={role.slug}>
                <h3 className="text-sm font-semibold mb-3">{role.label}</h3>
                <div className="grid grid-cols-2 gap-2">
                  {DASHBOARD_COMPONENT_KEYS.filter((key) => {
                    const restriction = COMPONENT_ROLE_RESTRICTIONS[key];
                    return !restriction || restriction.includes(role.slug);
                  }).map((key) => {
                    const id = `${role.slug}-${key}`;
                    return (
                      <div
                        key={key}
                        className="flex items-center gap-2 rounded-md border px-3 py-2"
                      >
                        <Checkbox
                          id={id}
                          checked={isChecked(role.slug, key)}
                          onCheckedChange={() => toggleKey(role.slug, key)}
                          data-testid={`checkbox-visibility-${role.slug}-${key}`}
                        />
                        <Label htmlFor={id} className="text-sm cursor-pointer select-none">
                          {DASHBOARD_COMPONENT_LABELS[key]}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saveMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading || saveMutation.isPending} data-testid="button-save-visibility">
            {saveMutation.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
